// The gateway's public manifest is the only source of layers (atlas-map spec). An entry is drawn
// only if it also has a style module; a style without an entry is never drawn.

import { GATEWAY } from "./config";
import type { LayerStyle } from "./styles";

export interface AtlasEntry {
  id: string;
  title: string;
  url: string;
  licence: string;
  licence_url: string;
  attribution: string;
  as_of: string;
  uses?: string[];
  category?: string;
}

export interface PublicManifest {
  generated_at?: string;
  layers: AtlasEntry[];
  basemap?: { url: string; attribution: string };
}

export interface StyledLayer {
  entry: AtlasEntry;
  style: LayerStyle;
}

export async function fetchManifest(fetcher: typeof fetch = fetch): Promise<PublicManifest> {
  const res = await fetcher(`${GATEWAY}/manifest.json`);
  if (!res.ok) throw new Error(`manifest: HTTP ${res.status}`);
  return (await res.json()) as PublicManifest;
}

/**
 * Pairs entries with styles in the styles' draw order. Unstyled ids are warned about and dropped;
 * an entry missing its provenance fields is dropped too, since it could not be shown honestly.
 */
export function selectStyled(manifest: PublicManifest, styles: ReadonlyMap<string, LayerStyle>, warn: (msg: string) => void = console.warn): StyledLayer[] {
  const byId = new Map<string, AtlasEntry>();
  for (const entry of manifest.layers ?? []) {
    if (!styles.has(entry.id)) {
      warn(`atlas: no style module for manifest layer "${entry.id}"; not drawn`);
      continue;
    }
    if (!entry.attribution || !entry.licence || !entry.as_of || !entry.url) {
      warn(`atlas: manifest layer "${entry.id}" lacks attribution, licence, as_of or url; not drawn`);
      continue;
    }
    byId.set(entry.id, entry);
  }
  const out: StyledLayer[] = [];
  for (const [id, style] of styles) {
    const entry = byId.get(id);
    if (entry) out.push({ entry: { ...entry, url: new URL(entry.url, location.href).href }, style });
  }
  return out;
}
